import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Radar,
    RadarChart as RechartsRadarChart,
    PolarGrid,
    Legend,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    Text,
} from 'recharts';
import { colors } from '../../../../shared/constants/constants';

/* eslint-disable complexity, react/no-unstable-nested-components */
function RadarChart({
    chartProperties,
    dataPoints,
    groupLabels,
    legendFormatter,
}) {
    const {
        colorPalette = colors,
        legendProperties,
        polarAngleAxisProperties,
        polarGridProperties,
        polarRadiusAxisProperties,
        radarChartProperties,
        radarProperties,
        responsiveContainerProperties,
        showRadiusAxis,
        showLegend,
    } = chartProperties;

    const axisLabel = useCallback(
        ({ payload, ...rest }) => (
            <Text {...rest} verticalAnchor="middle">
                {legendFormatter(payload.value)}
            </Text>
        ),
        [legendFormatter]
    );

    if (!dataPoints.length) {
        return null;
    }

    return (
        <div className="radar-chart">
            <ResponsiveContainer {...responsiveContainerProperties}>
                <RechartsRadarChart
                    {...radarChartProperties}
                    data={dataPoints}
                    cy="46%"
                    outerRadius="85%"
                >
                    <PolarGrid {...polarGridProperties} />
                    <PolarAngleAxis
                        {...polarAngleAxisProperties}
                        dataKey="metric"
                        tick={axisLabel}
                    />
                    {showRadiusAxis && (
                        <PolarRadiusAxis {...polarRadiusAxisProperties} />
                    )}
                    {showLegend && (
                        <Legend
                            {...legendProperties}
                            formatter={legendFormatter}
                        />
                    )}
                    {groupLabels.map((groupLabel, index) => (
                        <Radar
                            {...radarProperties}
                            key={groupLabel}
                            dataKey={groupLabel}
                            fill={colorPalette[index % colorPalette.length]}
                        />
                    ))}
                </RechartsRadarChart>
            </ResponsiveContainer>
        </div>
    );
}

RadarChart.propTypes = {
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataPoints: PropTypes.instanceOf(Array),
    groupLabels: PropTypes.instanceOf(Array),
    legendFormatter: PropTypes.func.isRequired,
};

RadarChart.defaultProps = {
    dataPoints: [],
    groupLabels: [],
};

export default React.memo(RadarChart, (prevProps, nextProps) =>
    _.isEqual(prevProps.dataPoints, nextProps.dataPoints)
);
